import { useEffect, useState } from 'react';
import Reveal from './Reveal';
import { STUDIO } from '@/content/studio';
import { ETERNITY } from '@/content/eternity';
import { useSettings } from '@/lib/useSettings';
import { api } from '@/lib/api';
import { BuildVideo } from '@/types';
import { track } from '@/lib/analytics';

/**
 * Band 3 — MAKING-OF. Black band. Mirrors the channel's latest 3 videos (worker
 * RSS, cached) so the work is actually shown, not just linked. Keeps a prominent
 * "Watch on YouTube" channel link, and a clean "coming soon" state when the
 * channel has no videos yet.
 */

const MakingOfBand = () => {
    const settings = useSettings();
    const [videos, setVideos] = useState<BuildVideo[]>([]);

    useEffect(() => {
        let cancelled = false;
        api.getBuildVideos().then((r) => { if (!cancelled && r.data) setVideos(r.data.videos); });
        return () => { cancelled = true; };
    }, []);

    return (
        <section className="hb-making" aria-label="Making-of series">
            <Reveal>
                <p className="hb-making-label">THE MAKING-OF SERIES</p>
                <h2 className="hb-making-heading">{STUDIO.makingOfHeading}</h2>
                <p className="hb-making-body">{STUDIO.makingOfBody}</p>
            </Reveal>

            <Reveal delay={0.1}>
                {videos.length > 0 ? (
                    <div className="hb-vid-grid">
                        {videos.map((v) => (
                            <a key={v.id} className="hb-vid" href={v.url} target="_blank" rel="noopener noreferrer"
                               onClick={() => track('video_click', { id: v.id })}>
                                <span className="hb-vid-thumb">
                                    <img src={v.thumbnail} alt="" loading="lazy" />
                                    <span className="hb-vid-play" aria-hidden="true" />
                                </span>
                                <span className="hb-vid-title">{v.title}</span>
                            </a>
                        ))}
                    </div>
                ) : (
                    <p className="hb-making-soon">Episodes coming soon — the first one drops here.</p>
                )}

                <a href={settings.socialYoutube} target="_blank" rel="noopener noreferrer" className="hb-making-yt">
                    WATCH ON YOUTUBE <span aria-hidden="true">→</span>
                </a>
                <p className="hb-making-patreon">
                    {ETERNITY.patreonContext}{' '}
                    <a href={settings.socialPatreon} target="_blank" rel="noopener noreferrer" className="hb-making-patreon-link">
                        PATREON →
                    </a>
                </p>
            </Reveal>

            <style>{`
                .hb-making { background: #000; padding: var(--by) var(--bx); }
                .hb-making-label {
                    font-family: var(--font-mono); font-size: 0.66rem; font-weight: 700;
                    letter-spacing: 0.28em; color: var(--color-accent-secondary); margin: 0 0 1.1rem;
                }
                .hb-making-heading {
                    font-family: var(--font-display); font-weight: 700;
                    font-size: clamp(2.6rem, 7vw, 6rem); line-height: 0.92; text-transform: uppercase;
                    color: #fff; margin: 0 0 1.1rem;
                }
                .hb-making-body {
                    font-family: var(--font-family); font-size: clamp(0.98rem, 1.4vw, 1.15rem);
                    line-height: 1.55; color: var(--color-text-tertiary); max-width: 48ch;
                    margin: 0 0 clamp(2.4rem, 6vw, 3.5rem);
                }

                /* latest videos */
                .hb-vid-grid {
                    display: grid; grid-template-columns: repeat(3, 1fr);
                    gap: clamp(1rem, 2vw, 1.6rem); margin: 0 0 clamp(2.2rem, 5vw, 3.2rem);
                }
                @media (max-width: 760px) { .hb-vid-grid { grid-template-columns: 1fr; max-width: 30rem; } }
                .hb-vid { display: block; color: #fff; text-decoration: none; }
                .hb-vid-thumb {
                    position: relative; display: block; aspect-ratio: 16 / 9; overflow: hidden;
                    background: #111; border: 1px solid rgba(255,255,255,0.12);
                }
                .hb-vid-thumb img {
                    width: 100%; height: 100%; object-fit: cover; display: block;
                    transition: transform 0.45s ease, opacity 0.45s ease; opacity: 0.92;
                }
                .hb-vid:hover .hb-vid-thumb img { transform: scale(1.045); opacity: 1; }
                .hb-vid-play {
                    position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
                    background: radial-gradient(circle at center, rgba(0,0,0,0.18), rgba(0,0,0,0.42));
                    transition: background 0.3s ease;
                }
                .hb-vid-play::after {
                    content: ''; width: 0; height: 0;
                    border-style: solid; border-width: 0.7rem 0 0.7rem 1.15rem;
                    border-color: transparent transparent transparent #fff;
                    filter: drop-shadow(0 2px 8px rgba(0,0,0,0.6)); transition: transform 0.3s ease;
                }
                .hb-vid:hover .hb-vid-play { background: radial-gradient(circle at center, rgba(220,38,38,0.25), rgba(0,0,0,0.45)); }
                .hb-vid:hover .hb-vid-play::after { transform: scale(1.12); }
                .hb-vid-title {
                    display: block; margin: 0.75rem 0 0; font-family: var(--font-family);
                    font-size: 0.95rem; line-height: 1.38; color: #e5e5e5;
                }

                .hb-making-soon {
                    font-family: var(--font-mono); font-size: 0.82rem; letter-spacing: 0.1em;
                    color: var(--color-text-muted); margin: 0 0 clamp(2rem, 4vw, 2.8rem);
                }

                .hb-making-yt {
                    display: inline-flex; align-items: center; gap: 0.6rem;
                    font-family: var(--font-display); font-weight: 700;
                    font-size: clamp(1.8rem, 4.5vw, 3.2rem); line-height: 1; letter-spacing: 0.01em;
                    text-transform: uppercase; color: #fff; transition: color var(--transition-fast);
                }
                .hb-making-yt:hover { color: var(--color-accent-primary); }
                .hb-making-patreon {
                    font-family: var(--font-family); font-size: 0.92rem;
                    color: var(--color-text-tertiary); margin: clamp(1.4rem, 3vw, 2rem) 0 0;
                }
                .hb-making-patreon-link {
                    display: inline-block; padding: 0.6rem 0; font-weight: 700; font-size: 0.82rem;
                    letter-spacing: 0.14em; color: #fff; border-bottom: 1px solid rgba(255, 255, 255, 0.4);
                }
                .hb-making-patreon-link:hover { color: var(--color-accent-secondary); border-color: var(--color-accent-secondary); }
            `}</style>
        </section>
    );
};

export default MakingOfBand;
